import { useState } from 'react';
import { useCardsStore } from '../../stores/cards-store';
import { useDB } from '../../hooks/useDB';
import { useDecks, useInvalidateDecks } from '../../hooks/useDecks';
import { addCardFromNote } from '../../lib/cards/note-card-service';
import { Zap, X } from 'lucide-react';
import { useI18n } from '../../lib/i18n/i18n-store';

import './FlashcardCreationModal.css';
interface FlashcardCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultBack: string;
  sourceNoteId: string;
  /** Pre-filled note title to auto-suggest a deck name */
  sourceNoteTitle?: string;
}

export function FlashcardCreationModal({
  isOpen,
  onClose,
  defaultBack,
  sourceNoteId,
  sourceNoteTitle = '',
}: FlashcardCreationModalProps) {
  const { t } = useI18n()
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [deckName, setDeckName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(isOpen);
  const { addCard } = useCardsStore();
  const { service: stateService, services } = useDB();
  const { data: decks = [] } = useDecks();
  const invalidateDecks = useInvalidateDecks();

  // No pre-selected text (e.g. opened from the Dashboard's quick-action rather
  // than a text selection in the editor) — the answer becomes a normal editable
  // field instead of a read-only preview of the selection.
  const hasSelection = defaultBack.trim().length > 0

  // Auto-suggest deck name from note title when the modal transitions to open
  // (adjust state during render instead of an effect)
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setDeckName(sourceNoteTitle ? `${sourceNoteTitle} — Cards` : '');
      setFront('');
      setBack('');
      setError(null);
      setIsSaving(false);
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const backValue = hasSelection ? defaultBack.trim() : back.trim();
    if (!front.trim() || !deckName.trim() || !backValue) return;
    if (!stateService || !services) { setError('La base de datos aún no está lista'); return }

    setIsSaving(true);
    setError(null);
    try {
      // Create the card in the REAL deck/FSRS system so it actually gets
      // scheduled and shows up in review sessions. Local writes are
      // authoritative; the deck's upload is queued for the next sync.
      const result = await addCardFromNote({
        stateService,
        uploads: services.deckUploads,
        deckName,
        front,
        back: backValue,
      });

      // Secondary: record the note↔card link that powers "Flashcards vinculadas"
      // in the note editor. Best-effort — this index lives on Drive, so it must
      // never cost the user a card that is already saved locally.
      try {
        await addCard({
          front: front.trim(),
          back: backValue,
          source_note_id: sourceNoteId,
          deck_id: result.deckInstanceId,
          fsrs_state: { status: 'new' },
        });
      } catch (linkErr) {
        console.warn('[FlashcardModal] Card created; note link deferred:', linkErr);
      }

      invalidateDecks();
      onClose();
    } catch (err) {
      console.error('[FlashcardModal] Save failed:', err);
      setError(err instanceof Error ? err.message : 'No se pudo crear la tarjeta');
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fcm-overlay" onClick={onClose}>
      <div className="fcm-panel" onClick={e => e.stopPropagation()}>
        <div className="fcm-header">
          <div className="fcm-header-left">
            <Zap size={16} className="fcm-icon" />
            <div>
              <h2 className="fcm-title">{t('flashcard.title')}</h2>
              <p className="fcm-subtitle">
                {hasSelection ? 'El texto seleccionado se usará como respuesta' : `Desde "${sourceNoteTitle || 'esta nota'}"`}
              </p>
            </div>
          </div>
          <button className="btn btn--ghost btn--icon fcm-close" onClick={onClose} aria-label={t('common.close')}><X size={16} /></button>
        </div>

        <form onSubmit={handleSave} className="fcm-body">
          {/* Deck: pick an existing one so cards join a real deck, or type a new name */}
          <div className="fcm-field">
            <label className="fcm-label">{t('flashcard.targetDeck')}</label>
            {decks.length > 0 && (
              <div className="fcm-deck-chips">
                {decks.slice(0, 6).map(d => (
                  <button
                    key={d.instance.id}
                    type="button"
                    className={`chip fcm-deck-chip ${deckName.trim().toLowerCase() === d.instance.deck_name.trim().toLowerCase() ? 'is-active' : ''}`}
                    onClick={() => setDeckName(d.instance.deck_name)}
                  >
                    {d.instance.deck_name}
                  </button>
                ))}
              </div>
            )}
            <input
              className="field fcm-input"
              type="text"
              value={deckName}
              onChange={e => setDeckName(e.target.value)}
              placeholder={t('flashcard.deckPlaceholder')}
              required
            />
            <span className="fcm-hint">
              Elige un mazo existente o escribe un nombre nuevo. La tarjeta entra al repaso espaciado de inmediato.
            </span>
          </div>

          {error && <div className="fcm-error">{error}</div>}

          {/* Question */}
          <div className="fcm-field">
            <label className="fcm-label">{t('flashcard.front')}</label>
            <textarea
              autoFocus
              value={front}
              onChange={e => setFront(e.target.value)}
              placeholder={hasSelection ? '¿Cuál es el concepto del texto seleccionado?' : '¿Qué pregunta quieres poder repasar?'}
              rows={3}
              className="field fcm-textarea"
              required
            />
          </div>

          {/* Answer: read-only preview of the selection, or an editable field
              when there's no selection to draw from (e.g. Dashboard quick-action) */}
          <div className="fcm-field">
            <label className="fcm-label">{t('flashcard.back')}</label>
            {hasSelection ? (
              <div className="fcm-preview">{defaultBack}</div>
            ) : (
              <textarea
                value={back}
                onChange={e => setBack(e.target.value)}
                placeholder={t('flashcard.backPlaceholder')}
                rows={3}
                className="field fcm-textarea"
                required
              />
            )}
          </div>

          <div className="fcm-footer">
            <button type="button" onClick={onClose} className="btn btn--outline fcm-btn-cancel">{t('common.cancel')}</button>
            <button
              type="submit"
              disabled={isSaving || !front.trim() || !deckName.trim() || (!hasSelection && !back.trim())}
              className="btn btn--soft fcm-btn-submit"
            >
              {isSaving ? 'Guardando…' : '⚡ Guardar Card'}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
}
